// hooks/useBossQuest.js
// React hook + helpers for the weekly Boss Quest document.
// Subscribes to this week's boss quest; supports claim; includes auto-generation helper.

import { useEffect, useState } from "react";
import {
  claimBossQuestReward,
  orchestrateBossQuest,
} from "../api/systemApi";
import {
  getWeekId as getBossWeekId,
} from "../utils/bossQuestUtils";
import {
  getBossQuest,
  subscribeToBossQuest,
  upsertBossQuest,
} from "../api/supabaseData";

/**
 * useBossQuest
 * Subscribes to users/{uid}/bossQuests/{YYYY-WW} for the CURRENT week.
 * Returns: { bossQuest, weekId, createOrUpdateBossQuest, claimBossQuest }
 */
export function useBossQuest(uid, options = {}) {
  const { onProfileUpdate, timeZone = null } = options;
  const [bossQuest, setBossQuest] = useState(null);

  // compute current week id on each render (safe + tiny)
  const weekId = getBossWeekId(new Date(), timeZone);

  useEffect(() => {
    if (!uid) return;
    return subscribeToBossQuest(uid, weekId, (quest) => {
      setBossQuest(quest);
    });
  }, [uid, weekId]);

  // Create or update (manual)
  const createOrUpdateBossQuest = async ({ title, desc, xp }) => {
    if (!uid) return;
    const payload = {
      title: String(title || "Weekly Boss Challenge"),
      desc: String(desc || ""),
      xp: Number(xp) || 200,
      status: "pending",
      claimed: false,
      source: "manual",
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    const savedQuest = await upsertBossQuest(uid, weekId, payload);
    setBossQuest(savedQuest);
  };

  // Claim & award XP (idempotent-ish: if already claimed, does nothing)
  const claimBossQuest = async () => {
    if (!uid) return;
    const result = await claimBossQuestReward(weekId);
    if (result.profile && typeof onProfileUpdate === "function") {
      onProfileUpdate(result.profile);
    }
    if (result.bossQuest) {
      setBossQuest(result.bossQuest);
      return;
    }

    const existing = await getBossQuest(uid, weekId);
    if (existing) {
      setBossQuest(existing);
    }
  };

  return { bossQuest, weekId, createOrUpdateBossQuest, claimBossQuest };
}

/**
 * autoGenerateBossQuestIfNeeded
 * - Runs on client when the app opens
 * - If it's Monday (local), user's profile has autoBossQuest enabled (checked by caller),
 *   and the boss quest for the current ISO week doesn't exist yet:
 *     → generate from history and write it once.
 * - If there is no history last week, it does nothing.
 */
export async function autoGenerateBossQuestIfNeeded(uid, userLevel) {
  try {
    if (!uid) return;
    await orchestrateBossQuest({
      mode: "auto",
      userLevel,
    });
  } catch (err) {
    // Non-fatal: log and continue
    console.error("autoGenerateBossQuestIfNeeded error:", err);
  }
}
